/**
 * Attendance Store Slice
 *
 * Manages attendance records in the Zustand store.
 * Data is synced from Firestore via useAttendanceSync.
 */
import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { AttendanceRecord, AttendanceStatus } from './types';

export interface AttendanceSlice {
  /** All attendance records for the current project (real-time synced) */
  attendanceRecords: AttendanceRecord[];
  attendanceActions: {
    /** Replace all attendance records (called by sync hook) */
    setAttendanceRecords: (records: AttendanceRecord[]) => void;
    /** Update a single actor's status locally (optimistic for SM overrides) */
    updateActorStatus: (
      eventId: string,
      actorId: string,
      status: AttendanceStatus,
      overrideUid?: string
    ) => void;
    /** Partially update a record's fields (e.g. coverCredits) */
    updateRecord: (eventId: string, updates: Partial<AttendanceRecord>) => void;
    /** Clear attendance data (project switch) */
    clearAttendance: () => void;
  };
}

export const createAttendanceSlice: StateCreator<
  AppState & AttendanceSlice,
  [["zustand/immer", never]],
  [],
  AttendanceSlice
> = (set) => ({
  attendanceRecords: [],
  attendanceActions: {
    setAttendanceRecords: (records) => set(state => {
      state.attendanceRecords = records;
    }),

    updateActorStatus: (eventId, actorId, status, overrideUid) => set(state => {
      const record = state.attendanceRecords.find(r => r.eventId === eventId);
      if (!record) return; // Record must exist from sync

      const existing = record.actors[actorId];
      record.actors[actorId] = {
        status,
        // Preserve existing signInTime if present
        ...(existing?.signInTime ? { signInTime: existing.signInTime } : {}),
        ...(existing?.called !== undefined ? { called: existing.called } : {}),
        ...(existing?.source ? { source: existing.source } : {}),
        // Add override metadata if SM is overriding
        ...(overrideUid ? {
          overriddenBy: overrideUid,
          overriddenAt: Date.now()
        } : {}),
      };
    }),

    updateRecord: (eventId, updates) => set(state => {
      const record = state.attendanceRecords.find(r => r.eventId === eventId);
      if (!record) return;
      Object.assign(record, updates);
    }),

    clearAttendance: () => set(state => {
      state.attendanceRecords = [];
    }),
  },
});
